import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles } from "lucide-react";
import { KnowledgeDraft } from "./KnowledgeDraft";

const OPTIONS = [["conversational", "Make more conversational"], ["professional", "Make more professional"], ["shorten", "Shorten the email"],
  ["experienced", "Tailor to an experienced insurance professional"], ["new_to_insurance", "Tailor to someone new to insurance"],
  ["next_step", "Add a clear next step"], ["subject", "Improve the subject line"]];

/** AI Personalize: proposes a revision for the recruiter to review. Nothing changes until "Use this version". */
export function PersonalizeBar({ subject, bodyhtml, onAccept, personId, opportunityId, onApplyKnowledge }) {
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const run = async (option) => {
    setBusy(true); setDraft(null);
    try { const { data } = await api.post("/email-center/personalize", { option, subject, bodyhtml }); setDraft(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-md border border-violet-200 bg-violet-50/40 p-2" data-testid="personalize-bar">
      <div className="flex flex-wrap items-center gap-2">
        <Sparkles className="w-4 h-4 text-violet-600" /><span className="text-xs font-semibold text-slate-700">Personalize This Email</span>
        <Select value="" onValueChange={run} disabled={busy || !bodyhtml}>
          <SelectTrigger className="h-8 w-72 text-xs bg-white" data-testid="personalize-option"><SelectValue placeholder={busy ? "Working…" : "Choose a change"} /></SelectTrigger>
          <SelectContent className="bg-white">{OPTIONS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`personalize-opt-${k}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-[11px] text-slate-400">AI draft. Review it before sending.</span>
      </div>
      {draft && (
        <div className="mt-2 rounded border border-slate-200 bg-white p-2" data-testid="personalize-review">
          <p className="text-xs"><span className="text-slate-400">Subject:</span> {draft.subject}</p>
          <div className="prose prose-sm max-w-none max-h-56 overflow-auto border-t border-slate-100 mt-1 pt-1" dangerouslySetInnerHTML={{ __html: draft.bodyhtml }} />
          {draft.missing?.length > 0 && <p className="text-[11px] text-amber-700 mt-1" data-testid="personalize-missing">Information needed (not invented): {draft.missing.join(", ")}</p>}
          {draft.lost_merge_fields?.length > 0 && <p className="text-[11px] text-rose-700 mt-1" data-testid="personalize-lost">Removed merge fields: {draft.lost_merge_fields.join(", ")}. Check before using.</p>}
          <div className="flex justify-end gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => setDraft(null)} data-testid="personalize-discard">Discard</Button>
            <Button size="sm" onClick={() => { onAccept(draft); setDraft(null); toast.success("Revision applied. Review before sending."); }} className="bg-brand hover:bg-brand-strong" data-testid="personalize-accept">Use this version</Button>
          </div>
        </div>
      )}
      {onApplyKnowledge && <KnowledgeDraft subject={subject || ""} bodyhtml={bodyhtml || ""} personId={personId} opportunityId={opportunityId} onApply={onApplyKnowledge} />}
    </div>
  );
}
